import {Component, EventEmitter, Input, OnDestroy, OnInit, Output, inject} from '@angular/core';
import {DialogService} from "../../../shared/services/dialog.service";
import {DialogModule, TooltipModule} from "@syncfusion/ej2-angular-popups";
import {StepHeaderComponent} from "../../../shared/elements/step-header/step-header.component";
import {LanguageService} from "../../../shared/services/language.service";
import {StepContentComponent} from "../../../shared/elements/step-content/step-content.component";
import {NumericTextBoxModule, TextAreaModule, TextBoxModule} from "@syncfusion/ej2-angular-inputs";
import {DropDownListModule} from "@syncfusion/ej2-angular-dropdowns";
import {HeadlineComponent} from "../../../shared/elements/headline/headline.component";
import {TranslatePipe} from "../../../shared/pipes/translate.pipe";
import {FormButtonComponent} from "../../../shared/elements/form-button/form-button.component";
import {AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators, FormsModule} from "@angular/forms";
import {TooltipComponent} from "../../../shared/elements/tooltip/tooltip.component";
import {FormLabelComponent} from "../../../shared/elements/form-label/form-label.component";
import {FormTextareaComponent} from "../../../shared/elements/form-textarea/form-textarea.component";
import {FormInputComponent} from "../../../shared/elements/form-input/form-input.component";
import {arrayValidator} from "../../../shared/validators/array.directive";
import {FormDropdownComponent} from "../../../shared/elements/form-dropdown/form-dropdown.component";
import {DatePickerModule, DateTimePickerModule, TimePickerModule} from "@syncfusion/ej2-angular-calendars";
import {RadioButtonModule, CheckBoxModule} from "@syncfusion/ej2-angular-buttons";
import {FormRadioComponent} from "../../../shared/elements/form-radio/form-radio.component";
import {FormNumericComponent} from "../../../shared/elements/form-numeric/form-numeric.component";
import {FormDateTimeComponent} from "../../../shared/elements/form-date-time/form-date-time.component";
import {FormUserGroupMailComponent} from "../../../shared/elements/form-user-group-mail/form-user-group-mail.component";
import {FormCheckboxComponent} from "../../../shared/elements/form-checkbox/form-checkbox.component";
import {FormDateComponent} from "../../../shared/elements/form-date/form-date.component";
import {ContentComponent} from "../../../shared/elements/content/content.component";
import {Subject, takeUntil} from 'rxjs';
import {ToasterService} from "../../../shared/services/toaster.service";
import {buildRecurrenceString, recalculateTimezoneData, validateRecurrencePattern} from '../mapper/form-to-calendar.mapper';
import {calendarTimeStepMinutes, decodeRrule, endTimeCalculation, formatTimeAndMinuteToDate, formatTimeIgnoringOffset, getEndDateTimeOneHourLater} from '../mapper/calendar-helper.mapper';
import {ScheduleComponent} from '@syncfusion/ej2-angular-schedule';
import {CalendarStore} from '../stores/calendar-store';
import {CalendarEventResource} from '../../../shared/types/calendar-types/calendarEventResource';
import {CalendarEventUpdateRequest} from '../../../shared/types/calendar-types/calendarEventUpdateRequest';
import {createCalendarEventUpdateRequest} from '../mapper/calendar-api.mapper';
import {FormTimeComponent} from '../../../shared/elements/form-time/form-time.component';
import {ProfileService} from '../../../shared/services/profile.service';

@Component({
    selector: 'app-dialog-calendar-event',
    imports: [
        DialogModule,
        StepHeaderComponent,
        StepContentComponent,
        TooltipModule,
        TextBoxModule,
        DropDownListModule,
        HeadlineComponent,
        TranslatePipe,
        FormButtonComponent,
        TooltipComponent,
        ReactiveFormsModule,
        FormsModule,
        FormLabelComponent,
        FormTextareaComponent,
        FormInputComponent,
        TextAreaModule,
        FormDropdownComponent,
        DatePickerModule,
        TimePickerModule,
        DateTimePickerModule,
        NumericTextBoxModule,
        RadioButtonModule,
        CheckBoxModule,
        FormRadioComponent,
        FormNumericComponent,
        FormDateTimeComponent,
        FormUserGroupMailComponent,
        FormCheckboxComponent,
        FormDateComponent,
        ContentComponent,
        FormTimeComponent
    ],
    templateUrl: './dialog-calendar-event.component.html',
    styleUrl: './dialog-calendar-event.component.scss'
})

export class DialogCalendarEventComponent implements OnInit, OnDestroy {
  public dialogService: DialogService = inject(DialogService);
  private languageService: LanguageService = inject(LanguageService);
  private formBuilder: FormBuilder = inject(FormBuilder);
  private toasterService: ToasterService = inject(ToasterService);
  private calendarStore: InstanceType<typeof CalendarStore> = inject(CalendarStore);
  private profileService: ProfileService = inject(ProfileService);

  @Input() public set calendarEvent(value: CalendarEventResource | null) {
    //if we are creating a new event in the mini calendar from the Dashboard we will pass value as null. 
    // If we are editing an event in the Dashboard or we are creating/editing an event from the Full Screen calendar we pass the value
    this._initialEvent = value;
  } 
  public get calendarEvent(): CalendarEventResource | null {
    return this._initialEvent;
  }
  @Input() public visible: boolean = false;
  @Input() public schedulerRef: ScheduleComponent | null = null;
  @Output() public visibleChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  private _initialEvent: CalendarEventResource | null = null;
  public steps: string[] = [this.languageService.getTranslationByKey('CALEVENTDETAILS'), this.languageService.getTranslationByKey('CALEVENTDATELOC'), this.languageService.getTranslationByKey('CALEVENTGUESTS')];
  public activeStep: number = 0;
  public visibleQuickDialog: boolean = false;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public priorityOptions: any[] = [{id: 'high', name: this.languageService.getTranslationByKey('HIGH')}, {id: 'medium', name: this.languageService.getTranslationByKey('MEDIUM')}, {id: 'low', name: this.languageService.getTranslationByKey('LOW')}];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public typeOptions: any[] = [{id: 'days', name: this.languageService.getTranslationByKey('DAYS')}, {id: 'weeks', name: this.languageService.getTranslationByKey('WEEKS')}, {id: 'months', name: this.languageService.getTranslationByKey('MONTHS')}];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public endOptions: any[] = [{id: 'after', name: this.languageService.getTranslationByKey('ENDAFTER')}, {id: 'on', name: this.languageService.getTranslationByKey('ON')}, {id: 'never', name: this.languageService.getTranslationByKey('NEVER')}];
  public repeatTypeOptions: { id: string; name: string }[] = [
    {id: 'never', name: this.languageService.getTranslationByKey('NEVER')},
    {id: 'daily', name: this.languageService.getTranslationByKey('DAILY')},
    {id: 'weekly', name: this.languageService.getTranslationByKey('WEEKLY')},
    {id: 'monthly', name: this.languageService.getTranslationByKey('MONTHLY')},
    {id: 'yearly', name: this.languageService.getTranslationByKey('YEARLY')}
  ];
  public repeatEndOptions: { id: string; name: string }[] = [
    {id: 'never', name: this.languageService.getTranslationByKey('NEVER')},
    {id: 'until', name: this.languageService.getTranslationByKey('UNTIL')},
    {id: 'count', name: this.languageService.getTranslationByKey('COUNT')}
  ];
  public repeatUnit: string = 'DAYS';
  public showRepeatEvery: boolean = false;
  public weekdayOptions: { value: string; label: string }[] = [];

  public ordinalOptions: { id: string; name: string }[] = [
    {id: '1', name: this.languageService.getTranslationByKey('FIRST')},
    {id: '2', name: this.languageService.getTranslationByKey('SECOND')},
    {id: '3', name: this.languageService.getTranslationByKey('THIRD')},
    {id: '4', name: this.languageService.getTranslationByKey('FOURTH')},
    {id: '-1', name: this.languageService.getTranslationByKey('LAST')},
  ];

  public weekdayOptionsLong: { id: string; name: string }[] = [];
  public monthOptions: { id: number; name: string }[] = [];

  public events: Date[] = [];
  public eventsShowAll: boolean = false;
  public participantsUserIDs: number[] = [];
  public participantsGroupIDs: number[] = [];
  public participantsMails: string[] = [];
  public pushUserIDs: number[] = [];
  public pushGroupIDs: number[] = [];
  public calendarStepMinutes: number = calendarTimeStepMinutes();
  public calendarEventForm!: FormGroup;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public customTimezone: any;
  private selectedZone: string;
  private eventToUpdate: CalendarEventUpdateRequest | null = null;
  private destroy$: Subject<void> = new Subject<void>();
  
  // Track which sections have been edited
  public section0Edited: boolean = false; // Basic details (name, priority, description, location)
  public section1Edited: boolean = false; // Date/time and recurrence settings
  public section2Edited: boolean = false; // Participants

  public adaptDetails: boolean = false;
  public adaptDateLocation: boolean = false;
  public adaptGuests: boolean = false;

  public constructor() {
    this.customTimezone = Intl.supportedValuesOf('timeZone').map((zone: string) => ({name: zone, id: zone}));
    this.selectedZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  }

  public ngOnInit(): void {
    this.buildForm();
    this.resetSectionEditTracking();
    //if we are creating a new event in the mini calendar from the Dashboard we will pass value as null. 
    // If we are editing an event in the Dashboard or we are creating/editing an event from the Full Screen calendar we pass the value
    if (this._initialEvent) {
      this.populateForm(this._initialEvent);
    } else {
      this.initializeFormDefaults();
    }
    this.subscribeToFormChanges();
    this.setupSectionEditTracking();
    this.setLocalizedOptions();
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  //#region form handling
  private static readonly nonWhitespaceValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (typeof value !== 'string') {
      return {required: true};
    }

    return value.trim() === '' ? {required: true} : null;
  };

  private buildForm() {
    this.calendarEventForm = this.formBuilder.group({
      name: ['', [Validators.required, DialogCalendarEventComponent.nonWhitespaceValidator, Validators.minLength(1), Validators.maxLength(35)]],
      priority: ['medium', [Validators.required, arrayValidator(this.priorityOptions.map(item => item.id))]],
      description: ['', [Validators.required, Validators.minLength(1), Validators.maxLength(100)]],
      location: [''],
      onDateStart: [null],
      onDateEnd: [null],
      isWholeday: [false],
      startTimeZone: [this.selectedZone],
      repeatType: ['never', [arrayValidator(this.repeatTypeOptions.map(item => item.id))]],
      repeatEvery: [1],
      repeatEndType: ['never', [arrayValidator(this.repeatEndOptions.map(item => item.id))]],
      repeatDaysUntil: [null],
      repeatDaysCount: [null],
      repeatOn: [['MO']],
      monthlyRepeatMode: [true],
      monthlyRepeatDay: [14],
      monthlyOrdinal: ['1'],
      monthlyWeekday: ['MO'],
      repeatMonth: [new Date().getMonth() + 1],
      maxParticipants: [0],
      notifyBeforeMinutes: [this.calendarStore.schedulerOptions.defaultNotificationTime()],
      notifyAt: [null]
    });
  }

  private initializeFormDefaults(): void {
    const startAndEndTime = getEndDateTimeOneHourLater();
    const notifyBeforeMinutes = this.calendarStore.schedulerOptions.defaultNotificationTime();
    this.calendarEventForm.patchValue({
      onDateStart: startAndEndTime.start,
      onDateEnd: startAndEndTime.end,
      isWholeday: false,
      notifyBeforeMinutes: notifyBeforeMinutes
    });
  }

  //PROCESSING FORM DATA
  private populateForm(event: CalendarEventResource): void {
    const recurrenceRule = decodeRrule(event.recurrence.rrule ?? '');
    this.calendarEventForm.patchValue({
      name: event.name,
      priority: event.priority,
      description: event.description,
      onDateStart: new Date(event.timing.start_at),
      onDateEnd: endTimeCalculation(new Date(event.timing.start_at), event.timing.duration, event.timing.is_full_day),
      isWholeday: event.timing.is_full_day,
      notifyAt: (event.timing.notify_time !== null) ? formatTimeAndMinuteToDate(new Date(event.timing.start_at), event.timing.notify_time) : null,
      startTimeZone: event.timing.timezone || this.selectedZone,
      repeatType: recurrenceRule.repeatType,
      repeatEvery: recurrenceRule.repeatEvery,
      repeatEndType: recurrenceRule.repeatEndType ?? 'never',
      repeatDaysUntil: recurrenceRule.repeatDaysUntil,
      repeatDaysCount: recurrenceRule.repeatDaysCount,
      repeatOn: recurrenceRule.repeatOn,
      monthlyRepeatMode: recurrenceRule.monthlyRepeatMode,
      monthlyRepeatDay: recurrenceRule.monthlyRepeatDay,
      monthlyOrdinal: recurrenceRule.monthlyOrdinal ?? '1',
      monthlyWeekday: recurrenceRule.monthlyWeekday ?? 'MO',
      repeatMonth: recurrenceRule.repeatMonth ?? new Date().getMonth() + 1,
      maxParticipants: 50,
      // maxParticipants: event.maxParticipants, TODO is missing in api
      location: event.location,
      notifyBeforeMinutes: event.timing.notify_before === null ? 0 : event.timing.notify_before
    });
    this.participantsUserIDs = event.participants.user_ids ?? [];
    this.participantsGroupIDs = event.participants.group_ids ?? [];
  }

  private subscribeToFormChanges(): void {
    const form = this.calendarEventForm;

    //update onDateEnd when onDateStart changes
    form.get('onDateStart')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(startDate => {
        const endDateControl = form.get('onDateEnd');
        const allDayControl = form.get('isWholeday');
        if(allDayControl?.value) {
          if(endDateControl){
            const endDateNew = new Date(startDate);
            endDateNew.setHours(23, 59, 0, 0);
            endDateControl.setValue(endDateNew, {emitEvent: false});
          }
        } else {
          if (endDateControl) {
            const endDateNew = getEndDateTimeOneHourLater(startDate);
            endDateControl.setValue(endDateNew.end);
          }
        }
      });

    form.get('onDateEnd')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(endDate => {
        const startDateControl = form.get('onDateStart');
        const allDayControl = form.get('isWholeday');
        if(startDateControl && allDayControl) {
          if(allDayControl.value) {
            if(allDayControl.value) {
              const adjustedEnd = new Date(endDate);
              if (adjustedEnd < startDateControl.value) {
                adjustedEnd.setTime(startDateControl.value.getTime());
              }
              adjustedEnd.setHours(23, 59, 0, 0);
              form.get('onDateEnd')?.setValue(adjustedEnd, {emitEvent: false});
            }
            } else {
            // For recurring non-all-day events, limit duration to max 1439 minutes (23h 59m)
            const repeatType = form.get('repeatType')?.value;
            if (repeatType && repeatType !== 'never') {
              const duration = (endDate.getTime() - startDateControl.value.getTime()) / 60000;
              if (duration > 1439) {
              const maxEndDate = new Date(startDateControl.value.getTime() + 1439 * 60000);
              form.get('onDateEnd')?.setValue(maxEndDate, {emitEvent: false});
              return;
              }
            }
            if(startDateControl.value.getTime() >= endDate.getTime()) {
              const endDateNew = new Date(startDateControl.value.getTime() + 30 * 60000);
              form.get('onDateEnd')?.setValue(endDateNew);
            }
          }
        }
      });

    //clear timezones if whole day
    form.get('isWholeday')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(isAllDay => {
        if (isAllDay) {
            const currentStart = form.get('onDateStart')?.value;
            if (currentStart) {
              const newStart = new Date(currentStart);
              newStart.setHours(0, 0, 0, 0);
              const newEnd = new Date(currentStart);
              newEnd.setHours(23, 59, 0, 0);
              form.patchValue(
                {
                  repeatType: 'never',
                  onDateStart: newStart,
                  onDateEnd: newEnd
                },
                {emitEvent: true});
              form.patchValue(
                {
                  repeatType: 'never',
                  onDateStart: newStart
                },
                {emitEvent: true});
            } else {
              form.patchValue(
                {
                  repeatType: 'never',
                  notifyAt: null
                },
                {emitEvent: true});
            }
        } else {
          form.patchValue(
            {
              currentStart: getEndDateTimeOneHourLater().start,
            },
            {emitEvent: true}
          );
        }
      });

    //repeat dropdown event
    form.get('repeatType')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(type => {
        if (!type) return;
        if (type === 'never') {
          this.calendarEventForm.patchValue({repeatEndType: 'never'}, {emitEvent: false});
        }

        // Check if the selected type is one of the options which where should set the repeatEvery to 1
        const isRepeatable = this.repeatTypeOptions.some(opt => opt.id === type && type !== 'never');

        if (isRepeatable) {
          this.calendarEventForm.patchValue({repeatEvery: 1, isWholeday: false}, {emitEvent: true});

          // Limit duration to max 1439 minutes when switching to repeating event
          const startDateControl = form.get('onDateStart');
          const endDateControl = form.get('onDateEnd');
          if (startDateControl?.value && endDateControl?.value) {
            const duration = (endDateControl.value.getTime() - startDateControl.value.getTime()) / 60000;
            if (duration > 1439) {
              const maxEndDate = new Date(startDateControl.value.getTime() + 1439 * 60000);
              endDateControl.setValue(maxEndDate, {emitEvent: false});
            }
          }
        }
        switch (type) {
          case 'daily':
            this.repeatUnit = 'DAYS';
            break;
          case 'weekly':
            this.repeatUnit = 'WEEKS';
            break;
          case 'monthly':
            this.repeatUnit = 'MONTHS';
            break;
          case 'yearly':
            this.repeatUnit = 'YEARS';
            break;
        }
      });

    //repeatEndType handling
    form.get('repeatEndType')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(type => {
        const until = form.get('repeatDaysUntil');
        const count = form.get('repeatDaysCount');

        until?.clearValidators();
        count?.clearValidators();

        if (type === 'until') {
          if (!until?.value) {
            const start = form.get('onDateStart')?.value || new Date();
            const defaultUntil = new Date(start);
            defaultUntil.setDate(defaultUntil.getDate() + 7);
            until?.setValue(defaultUntil);
          }
          until?.setValidators([Validators.required]);
        } else if (type === 'count') {
          if (!count?.value) {
            count?.setValue(10);
          }
          count?.setValidators([Validators.required, Validators.min(1)]);
        }

        until?.updateValueAndValidity();
        count?.updateValueAndValidity();
    });
  }

  private setupSectionEditTracking(): void {
    const form = this.calendarEventForm;
    
    // Section 0: Basic details (name, priority, description, location)
    const section0Controls = ['name', 'priority', 'description', 'location'];
    section0Controls.forEach(controlName => {
      form.get(controlName)?.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.section0Edited = true;
        });
    });
    
    // Section 1: Date/time and recurrence settings
    const section1Controls = [
      'onDateStart', 'onDateEnd', 'isWholeday', 'startTimeZone',
      'repeatType', 'repeatEvery', 'repeatEndType', 'repeatDaysUntil', 'repeatDaysCount',
      'repeatOn', 'monthlyRepeatMode', 'monthlyRepeatDay', 'monthlyOrdinal',
      'monthlyWeekday', 'repeatMonth', 'notifyBeforeMinutes', 'notifyAt'
    ];
    section1Controls.forEach(controlName => {
      form.get(controlName)?.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.section1Edited = true;
        });
    });
    
    // Section 2: Participants - track array changes
    // Note: Since participants are bound with [(user)], [(groups)], etc., 
    // we'll track changes through property setters or direct monitoring
  }

  public toggleRepeatDay(dayCode: string): void {
    const control = this.calendarEventForm.get('repeatOn');
    if (!control) return;

    const current = control.value || [];

    if (current.includes(dayCode)) {
      control.setValue(current.filter((d: string) => d !== dayCode));
    } else {
      control.setValue([...current, dayCode]);
    }
  }

  //based on the language we translate the dropdown values with appropritate language based names
  private setLocalizedOptions(): void {
    const lang = this.languageService.getLanguage();
    const baseDate = new Date(Date.UTC(2024, 0, 1)); // Jan 1, 2024 = Monday is 100% a year with 1 Jan as monday

    const formatterShort = new Intl.DateTimeFormat(lang, {weekday: 'narrow'});
    const formatterLong = new Intl.DateTimeFormat(lang, {weekday: 'long'});

    const dayCodes = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'];
    const formatterMonth = new Intl.DateTimeFormat(lang, {month: 'long'});

    this.weekdayOptions = dayCodes.map((code, i) => ({
      value: code,
      label: formatterShort.format(new Date(baseDate.getTime() + i * 86400000))
    }));

    this.weekdayOptionsLong = dayCodes.map((code, i) => ({
      id: code,
      name: formatterLong.format(new Date(baseDate.getTime() + i * 86400000))
    }));

    this.monthOptions = Array.from({length: 12}, (_, i) => ({
      id: i + 1,
      name: formatterMonth.format(new Date(2024, i, 1))
    }));
  }
  //#endregion form handling

  //#region dialog handling
  public handleStepChange(targetStep: number): void {
    if (targetStep !== this.activeStep && !this.isCurrentStepValid()) {
      return;
    }

    if (this.activeStep === 1 && targetStep > this.activeStep) {
      //normalize the UTC
      const {startUTC, endUTC} = recalculateTimezoneData(this.calendarEventForm);
      //use the normalized UTC to validate
      const recurrenceError = validateRecurrencePattern(startUTC, endUTC, this.calendarEventForm.value);
      if (recurrenceError) {
        //put a toast message here??
        this.toasterService.error(this.languageService.getTranslationByKey('RECCURENCEERROR'), recurrenceError);
        return;
      }
    }
    this.activeStep = targetStep;
  }

  public close(): void {
    this.visibleChange.emit(false);
  }

    public save(): void {
    if (!this.isEventNameValid()) {
      this.markNameControlAsInvalidInteraction();
      return;
    }

    const isEditingMaster: boolean = !!this.calendarEvent?.recurrence.rrule && !this.calendarEvent?.override.parent_id;
    const isNewOverrideEvent: boolean = (!this.calendarEvent?.id || this.calendarEvent?.id === -1) && this.calendarEvent?.override.parent_id !== null;
    const isNewEvent: boolean = (!this.calendarEvent?.id || this.calendarEvent?.id === -1) && !this.calendarEvent?.override.parent_id;
    
    const newEvent = this.mapDefaultParamsFromForm();
    if (isNewEvent || isNewOverrideEvent) {
      this.calendarStore.createEvent(newEvent);
      this.visibleChange.emit(false);
    } else {
      if(isEditingMaster){
        if(this.calendarEvent!.recurrence.deleted_occurrences.length > 0 || this.calendarEvent!.recurrence.overridden_occurrences.length > 0){
          this.eventToUpdate = newEvent;
          this.initializeAdaptCheckboxes();
          this.visibleQuickDialog = true;
        } else {
          this.calendarStore.updateEvent(newEvent, this.calendarEvent!.id!, false, false, false);
          this.visibleChange.emit(false);
        }
      } else {
        // handles editing of existing override events
        this.calendarStore.updateEvent(newEvent, this.calendarEvent!.id!, false, false, false);
        this.visibleChange.emit(false);
      }
    }

  }
  //#endregion dialog handling

  //#region override alignment dialog handling
  private initializeAdaptCheckboxes(): void {
    this.adaptDetails = this.section0Edited;
    this.adaptDateLocation = this.section1Edited;
    this.adaptGuests = this.section2Edited;
  }

  public get allAdaptChecked(): boolean {
    const editedSections = [
      this.section0Edited && this.adaptDetails,
      this.section1Edited && this.adaptDateLocation,
      this.section2Edited && this.adaptGuests
    ];
    const enabledSections = [this.section0Edited, this.section1Edited, this.section2Edited];
    const checkedCount = editedSections.filter(Boolean).length;
    const enabledCount = enabledSections.filter(Boolean).length;
    return enabledCount > 0 && checkedCount === enabledCount;
  }

  public set allAdaptChecked(value: boolean) {
    if (this.section0Edited) this.adaptDetails = value;
    if (this.section1Edited) this.adaptDateLocation = value;
    if (this.section2Edited) this.adaptGuests = value;
  }

  public saveQuickDialog(): void {
    if(this.eventToUpdate !== null) {
      this.calendarStore.updateEvent(
        this.eventToUpdate, 
        this.calendarEvent!.id!, 
        this.adaptDetails, 
        this.adaptGuests,
        this.adaptDateLocation, 
      );
    }
    this.eventToUpdate = null;
    this.visibleQuickDialog = false;
    this.visibleChange.emit(false);
  }

  public cancelQuickDialog(): void {
    this.visibleQuickDialog = false;
  }
  //#endregion override alignment dialog handling

  private mapDefaultParamsFromForm(): CalendarEventUpdateRequest {
    const defaultUpdateEvent: CalendarEventUpdateRequest = createCalendarEventUpdateRequest();
    defaultUpdateEvent.name = this.calendarEventForm.controls['name'].value;
    defaultUpdateEvent.description = this.calendarEventForm.controls['description'].value;
    defaultUpdateEvent.location = this.calendarEventForm.controls['location'].value;
    defaultUpdateEvent.priority = this.calendarEventForm.controls['priority'].value;
    defaultUpdateEvent.calendar_id = this.calendarStore.selectedCalendarID() ?? -1;
    defaultUpdateEvent.creator.notifications = true;
    defaultUpdateEvent.timing.notify_before = 
    (this.calendarEventForm.controls['notifyBeforeMinutes'].value > 0 && !this.calendarEventForm.controls['isWholeday'].value) ?
      this.calendarEventForm.controls['notifyBeforeMinutes'].value :
      null;
    defaultUpdateEvent.timing.notify_time = (this.calendarEventForm.controls['isWholeday'].value) ? 
      formatTimeIgnoringOffset(this.calendarEventForm.controls['notifyAt'].value) :
      null;
    defaultUpdateEvent.timing.timezone = this.calendarEventForm.controls['startTimeZone'].value;
    defaultUpdateEvent.timing.start_at = this.calendarEventForm.controls['onDateStart'].value;
    defaultUpdateEvent.timing.duration = this.calculateEventDuration(
      this.calendarEventForm.controls['onDateStart'].value,
      this.calendarEventForm.controls['onDateEnd'].value,
      this.calendarEventForm.controls['isWholeday'].value);
    defaultUpdateEvent.timing.is_full_day = this.calendarEventForm.controls['isWholeday'].value;
    defaultUpdateEvent.participants.user_ids = this.adjustUserIDs(this.participantsUserIDs);
    defaultUpdateEvent.participants.group_ids = this.participantsGroupIDs;
    defaultUpdateEvent.participants.external_user_emails = this.participantsMails;
    defaultUpdateEvent.participants.notify_user_ids = this.pushUserIDs;
    defaultUpdateEvent.participants.notify_group_ids = this.pushGroupIDs;
    defaultUpdateEvent.recurrence.rrule = buildRecurrenceString({
      repeatType: this.calendarEventForm.controls['repeatType'].value,
      repeatEvery: this.calendarEventForm.controls['repeatEvery'].value,
      repeatEndType: this.calendarEventForm.controls['repeatEndType'].value,
      repeatDaysUntil: this.calendarEventForm.controls['repeatDaysUntil'].value,
      repeatDaysCount: this.calendarEventForm.controls['repeatDaysCount'].value,
      repeatOn: this.calendarEventForm.controls['repeatOn'].value,
      monthlyRepeatMode: this.calendarEventForm.controls['monthlyRepeatMode'].value,
      monthlyRepeatDay: this.calendarEventForm.controls['monthlyRepeatDay'].value,
      monthlyOrdinal: this.calendarEventForm.controls['monthlyOrdinal'].value,
      monthlyWeekday: this.calendarEventForm.controls['monthlyWeekday'].value,
      repeatMonth: this.calendarEventForm.controls['repeatMonth'].value,
    });

    // update the event with the existing data
    defaultUpdateEvent.recurrence.deleted_occurrences = this.calendarEvent?.recurrence.deleted_occurrences ?? [];
    defaultUpdateEvent.override.parent_id = this.calendarEvent?.override.parent_id ?? null;
    defaultUpdateEvent.override.original_at = this.calendarEvent?.override.original_at ?? null;
    return defaultUpdateEvent;
  }

  private calculateEventDuration(start: Date, end: Date, isAllDay: boolean): number {
    const daysSpanned = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
    if (isAllDay) {
      return 24 * 60 * daysSpanned;
    } else {
      return (end.getTime() - start.getTime()) / 60000;
    }
  }

  private adjustUserIDs(userIDs: number[]): number[] {
    const currentUserId = this.profileService.profile()?.user?.id;
    if (currentUserId === undefined) {
      return userIDs;
    }

    if(userIDs.includes(currentUserId)) {
      return userIDs;
    } else {
      return [...userIDs, currentUserId];
    }
  }

  public onParticipantsChange(): void {
    this.section2Edited = true;
  }

  private resetSectionEditTracking(): void {
    this.section0Edited = false;
    this.section1Edited = false;
    this.section2Edited = false;
  }

  public getEditedSections(): { section0: boolean; section1: boolean; section2: boolean } {
    return {
      section0: this.section0Edited,
      section1: this.section1Edited,
      section2: this.section2Edited
    };
  }

  public hasAnyEdits(): boolean {
    return this.section0Edited || this.section1Edited || this.section2Edited;
  }

  public timezoneSearch(searchTerm: string): void {
    this.customTimezone = Intl.supportedValuesOf('timeZone')
      .map((zone: string) => ({name: zone, id: zone}))
      .filter((zone: {name: string; id: string}) => zone.name.toLowerCase().includes(searchTerm.toLowerCase()));
  }

  private isEventNameValid(): boolean {
    const nameControl = this.calendarEventForm.get('name');
    const nameValue = nameControl?.value;

    if (typeof nameValue !== 'string') {
      return false;
    }

    return nameValue.trim() !== '';
  }

  private markNameControlAsInvalidInteraction(): void {
    const nameControl = this.calendarEventForm.get('name');
    if (!nameControl) {
      return;
    }

    nameControl.markAsDirty();
    nameControl.markAsTouched();
    nameControl.updateValueAndValidity({emitEvent: false});
  }

  private isCurrentStepValid(): boolean {
    const stepControlNames: Record<number, string[]> = {
      0: ['name', 'priority', 'description', 'location'],
      1: [
        'onDateStart',
        'onDateEnd',
        'isWholeday',
        'startTimeZone',
        'repeatType',
        'repeatEvery',
        'repeatEndType',
        'repeatDaysUntil',
        'repeatDaysCount',
        'repeatOn',
        'monthlyRepeatMode',
        'monthlyRepeatDay',
        'monthlyOrdinal',
        'monthlyWeekday',
        'repeatMonth',
        'notifyBeforeMinutes',
        'notifyAt'
      ],
      2: []
    };

    const controlsInCurrentStep = stepControlNames[this.activeStep] ?? [];
    let stepIsValid = true;

    controlsInCurrentStep.forEach(controlName => {
      const control = this.calendarEventForm.get(controlName);
      if (!control) {
        return;
      }

      control.updateValueAndValidity({emitEvent: false});
      if (control.invalid) {
        control.markAsDirty();
        control.markAsTouched();
        stepIsValid = false;
      }
    });

    if (this.activeStep === 0 && !this.isEventNameValid()) {
      this.markNameControlAsInvalidInteraction();
      stepIsValid = false;
    }

    return stepIsValid;
  }

}
